import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeedbackApiBase } from './feedback-api-base';
import {
  Comment,
  CommentRead,
  CreateCommentRequest,
  DataEnvelope,
  MarkCommentsReadRequest,
  UnreadCommentCounts,
  UnreadCommentsQuery,
} from '../models';

/**
 * Mirrors App\Http\Controllers\CommentController — the comment thread on a feedback
 * item. All endpoints live under `/api/v1`.
 *
 * Private comments and delete permissions are decided by the API from the caller's
 * token: the management app's wildcard token counts as an internal (team) caller,
 * while the widget's round-scoped token counts as a client. From a client token,
 * `isPrivate: true` is refused with 403, private comments are absent from reads, and
 * {@link deleteComment} is refused.
 */
@Injectable({ providedIn: 'root' })
export class CommentService extends FeedbackApiBase {
  /**
   * GET /v1/feedback/{feedbackId}/comments — the thread, oldest first.
   *
   * Private comments are included only for internal callers.
   */
  listComments(feedbackId: string): Observable<Comment[]> {
    return this.http
      .get<DataEnvelope<Comment[]>>(this.url(`feedback/${feedbackId}/comments`))
      .pipe(map((response) => response.data));
  }

  /** POST /v1/feedback/{feedbackId}/comments — adds a comment to the thread. */
  createComment(feedbackId: string, request: CreateCommentRequest): Observable<Comment> {
    return this.http
      .post<DataEnvelope<Comment>>(this.url(`feedback/${feedbackId}/comments`), request)
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedback/{feedbackId}/comments/read — records that this person has read
   * the thread up to now, which is what clears their unread badge.
   *
   * Call it when the thread is opened.
   */
  markCommentsRead(feedbackId: string, request: MarkCommentsReadRequest): Observable<CommentRead> {
    return this.http
      .post<DataEnvelope<CommentRead>>(this.url(`feedback/${feedbackId}/comments/read`), request)
      .pipe(map((response) => response.data));
  }

  /**
   * GET /v1/comments/unread — unread counts across a round, keyed by feedback id.
   *
   * Counts only cover feedback this person is part of the conversation on (authored, or
   * commented on), exclude their own comments, and — for a client-scoped caller —
   * exclude private comments, so a badge never points at something unopenable.
   */
  listUnreadCounts(query: UnreadCommentsQuery): Observable<UnreadCommentCounts> {
    return this.http
      .get<DataEnvelope<UnreadCommentCounts>>(this.url('comments/unread'), {
        params: this.buildParams({ ...query }),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * DELETE /v1/comments/{commentId} — soft-deletes a comment.
   *
   * Answers 204 with no body, hence `void`. Internal callers only.
   */
  deleteComment(commentId: string): Observable<void> {
    return this.http.delete<void>(this.url(`comments/${commentId}`));
  }
}
